import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useOpenerSource } from './hooks/useOpenerSource';

interface ExtensionMessage {
  type: string; // namespace:eventType (request, response, notification)
  namespace: string;
  method: string;
  data?: any;
  requestId?: string; // for requests and responses
}

interface ExtensionContextType {
  isLoading: boolean;
  isConnected: boolean;
  reconnect: () => Promise<boolean>;
  sendNotification: (namespace: string, method: string, data: any) => void;
  sendRequest: <T = any>(
    namespace: string,
    method: string,
    data: any
  ) => Promise<T>;
  onNotification: (
    namespace: string,
    method: string,
    callback: (data: any) => void
  ) => () => void;
}

const ExtensionContext = createContext<ExtensionContextType | null>(null);

interface ExtensionProviderProps extends PropsWithChildren {
  extensionIdByEnvironment: {
    staging: string;
    prod: string;
  };
}

export function ExtensionProvider({
  extensionIdByEnvironment,
  children,
}: ExtensionProviderProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const portRef = useRef<chrome.runtime.Port | null>(null);
  const pendingRequests = useRef<
    Map<
      string,
      { resolve: (value: any) => void; reject: (reason: any) => void }
    >
  >(new Map());
  const notificationListeners = useRef<Map<string, Set<(data: any) => void>>>(
    new Map()
  );

  const { openerSource } = useOpenerSource();

  console.log(
    '[ExtensionProvider] Initializing with opener source:',
    openerSource
  );

  // Determine which extension to connect to based on opener source
  const getExtensionId = useCallback(() => {
    if (openerSource === 'extension-staging') {
      return extensionIdByEnvironment.staging;
    }
    return extensionIdByEnvironment.prod;
  }, [openerSource, extensionIdByEnvironment]);

  // Handle incoming messages from the extension
  const handleMessage = useCallback((message: any) => {
    console.log(
      '[ExtensionProvider] Received message from extension:',
      message
    );

    // Check if this is a response to a request
    if (message.type?.endsWith(':response') && message.requestId) {
      console.log(
        '[ExtensionProvider] Processing response for request:',
        message.requestId
      );

      const pending = pendingRequests.current.get(message.requestId);
      if (pending) {
        if (message.error) {
          console.error(
            '[ExtensionProvider] Request failed:',
            message.requestId,
            message.error
          );
          pending.reject(message.error);
        } else {
          console.log(
            '[ExtensionProvider] Request succeeded:',
            message.requestId,
            message.data
          );
          pending.resolve(message.data);
        }
        pendingRequests.current.delete(message.requestId);
      } else {
        console.warn(
          '[ExtensionProvider] No pending request found for requestId:',
          message.requestId
        );
      }
      return;
    }

    // Check if this is a notification
    if (message.type?.endsWith(':notification')) {
      const namespace = message.type.split(':notification')[0];
      console.log(
        '[ExtensionProvider] Processing notification:',
        namespace,
        message.method
      );

      const key = `${namespace}:${message.method}`;
      const listeners = notificationListeners.current.get(key);
      if (listeners) {
        listeners.forEach((callback) => {
          try {
            callback(message.data);
          } catch (error) {
            console.error(
              '[ExtensionProvider] Error in notification listener:',
              error
            );
          }
        });
      } else {
        console.debug(
          '[ExtensionProvider] No listeners found for notification:',
          key
        );
      }
    }
  }, []);

  // Function to establish connection to extension
  const connectToExtension = useCallback((): boolean => {
    // Only connect if we have chrome runtime available
    if (!chrome?.runtime?.connect) {
      console.warn(
        '[ExtensionProvider] Chrome runtime not available - cannot connect to extension'
      );
      return false;
    }

    const extensionId = getExtensionId();
    console.log('[ExtensionProvider] Extension ID:', extensionId);

    if (!extensionId) {
      console.warn(
        '[ExtensionProvider] No extension ID available - cannot connect'
      );
      return false;
    }

    try {
      // Disconnect existing port if any
      if (portRef.current) {
        console.log('[ExtensionProvider] Disconnecting existing port');
        portRef.current.disconnect();
        portRef.current = null;
      }

      console.log('[ExtensionProvider] Creating port connection...');
      const newPort = chrome.runtime.connect(extensionId, {
        name: 'extension-provider',
      });

      newPort.onMessage.addListener(handleMessage);

      newPort.onDisconnect.addListener(() => {
        console.log('[ExtensionProvider] Extension port disconnected');
        const pendingCount = pendingRequests.current.size;
        if (pendingCount > 0) {
          console.warn(
            `[ExtensionProvider] Clearing ${pendingCount} pending requests due to disconnect`
          );
        }
        portRef.current = null;
        setIsConnected(false);

        // Clear any pending requests
        pendingRequests.current.forEach(({ reject }) => {
          reject(new Error('Extension disconnected'));
        });
        pendingRequests.current.clear();
      });

      portRef.current = newPort;
      console.log('[ExtensionProvider] Port saved, setting loading to false');
      setIsLoading(false);
      setIsConnected(true);
      console.log('[ExtensionProvider] Successfully connected to extension!');
      return true;
    } catch (error) {
      console.error(
        '[ExtensionProvider] Failed to connect to extension:',
        error
      );
      console.log('[ExtensionProvider] Setting loading to false due to error');
      setIsLoading(false);
      setIsConnected(false);
      return false;
    }
  }, [getExtensionId, handleMessage]);

  // Connect to the extension on mount
  useEffect(() => {
    console.log('[ExtensionProvider] Attempting to connect to extension...');
    connectToExtension();

    // Cleanup on unmount
    return () => {
      if (portRef.current) {
        console.log('[ExtensionProvider] Cleaning up - disconnecting port');
        portRef.current.disconnect();
        portRef.current = null;
        setIsConnected(false);
      }
    };
  }, [connectToExtension]);

  // Reconnect function that returns a promise
  const reconnect = useCallback(async (): Promise<boolean> => {
    console.log('[ExtensionProvider] Attempting to reconnect to extension...');
    setIsLoading(true);

    // Small delay to allow any cleanup
    await new Promise((resolve) => setTimeout(resolve, 100));

    const success = connectToExtension();
    return success;
  }, [connectToExtension]);

  const sendNotification = useCallback(
    (namespace: string, method: string, data: any) => {
      console.log(
        `[ExtensionProvider] Sending notification - namespace: ${namespace}, method: ${method}`,
        data
      );

      if (!portRef.current) {
        console.warn(
          '[ExtensionProvider] Cannot send notification: not connected to extension'
        );
        return;
      }

      const message: ExtensionMessage = {
        type: `${namespace}:notification`,
        namespace,
        method,
        data,
      };

      try {
        portRef.current.postMessage(message);
        console.log('[ExtensionProvider] Notification sent successfully');
      } catch (error) {
        console.error(
          '[ExtensionProvider] Failed to send notification:',
          error
        );
      }
    },
    []
  );

  const sendRequest = useCallback(
    <T = any,>(namespace: string, method: string, data: any): Promise<T> => {
      const requestId = crypto.randomUUID();
      console.log(
        `[ExtensionProvider] Sending request - requestId: ${requestId}, namespace: ${namespace}, method: ${method}`,
        data
      );

      if (!portRef.current) {
        console.error(
          '[ExtensionProvider] Cannot send request: not connected to extension'
        );
        return Promise.reject(new Error('Not connected to extension'));
      }

      const message: ExtensionMessage = {
        type: `${namespace}:request`,
        namespace,
        method,
        data,
        requestId,
      };

      return new Promise<T>((resolve, reject) => {
        // Store the promise handlers
        pendingRequests.current.set(requestId, { resolve, reject });
        console.log(
          `[ExtensionProvider] Request ${requestId} added to pending queue. Total pending: ${pendingRequests.current.size}`
        );

        try {
          portRef.current?.postMessage(message);
          console.log(
            `[ExtensionProvider] Request ${requestId} sent successfully`
          );

          // Add timeout for requests
          setTimeout(() => {
            if (pendingRequests.current.has(requestId)) {
              console.error(
                `[ExtensionProvider] Request ${requestId} timed out after 30 seconds`
              );
              pendingRequests.current.delete(requestId);
              reject(new Error('Request timeout'));
            }
          }, 30000); // 30 second timeout
        } catch (error) {
          console.error(
            `[ExtensionProvider] Failed to send request ${requestId}:`,
            error
          );
          pendingRequests.current.delete(requestId);
          reject(error);
        }
      });
    },
    []
  );

  const onNotification = useCallback(
    (
      namespace: string,
      method: string,
      callback: (data: any) => void
    ): (() => void) => {
      const key = `${namespace}:${method}`;
      if (!notificationListeners.current.has(key)) {
        notificationListeners.current.set(key, new Set());
      }
      notificationListeners.current.get(key)!.add(callback);

      console.log('[ExtensionProvider] Registered notification listener', {
        namespace,
        method,
        totalListeners: notificationListeners.current.get(key)!.size,
      });

      // Return unsubscribe function
      return () => {
        const listeners = notificationListeners.current.get(key);
        if (listeners) {
          listeners.delete(callback);
          if (listeners.size === 0) {
            notificationListeners.current.delete(key);
          }
        }
        console.log('[ExtensionProvider] Unregistered notification listener', {
          namespace,
          method,
        });
      };
    },
    []
  );

  const contextValue: ExtensionContextType = {
    isLoading,
    isConnected,
    reconnect,
    sendNotification,
    sendRequest,
    onNotification,
  };

  // Log when loading state changes
  useEffect(() => {
    console.log('[ExtensionProvider] Loading state changed:', isLoading);
  }, [isLoading]);

  return (
    <ExtensionContext.Provider value={contextValue}>
      {children}
    </ExtensionContext.Provider>
  );
}

export function useExtension() {
  const context = useContext(ExtensionContext);
  if (!context) {
    throw new Error('useExtension must be used within an ExtensionProvider');
  }
  return context;
}
